import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Column widths a person can drag, remembered per table.
 *
 * The queue is the densest screen in the product and every pharmacy reads it
 * differently: one cares about patient names that run long, another about
 * seeing four filiais at once. A fixed layout serves the average of those and
 * nobody in particular.
 *
 * **Widths live in the browser, not on the account.** It is a fact about this
 * person on this screen at this size, which is exactly the scope
 * `localStorage` has — and wrapped both ways, because a private window throws
 * and a table is never a reason for a page to fail.
 *
 * The drag runs on `pointermove` against the header's own left edge rather
 * than on deltas, so a fast drag that outruns the render loop still lands
 * where the pointer is instead of drifting behind it.
 */
export interface ResizableColumns {
  /** The width to apply, or `undefined` for a column nobody has resized. */
  widthOf(key: string): number | undefined
  /**
   * Starts a drag. `edge` says which side of the named column the handle
   * sits on: `right` (the usual) resizes it by its right edge; `left` resizes
   * it by its left edge, which is how the boundary before it can be dragged
   * from a column that itself carries no width.
   */
  onGrab(key: string, event: React.PointerEvent<HTMLElement>, edge?: 'left' | 'right'): void
  /** Whether a drag is in progress, for suppressing hover and selection. */
  resizing: boolean
}

/** Narrow enough to be useless, so a drag cannot hide a column entirely. */
const MIN_WIDTH = 56

export function useResizableColumns(tableKey: string): ResizableColumns {
  const [widths, setWidths] = useState<Readonly<Record<string, number>>>(() => {
    try {
      const stored = window.localStorage.getItem(`columns.${tableKey}`)
      return stored === null ? {} : (JSON.parse(stored) as Record<string, number>)
    } catch {
      return {}
    }
  })
  const [resizing, setResizing] = useState(false)
  const drag = useRef<
    { key: string; left: number; right: number; edge: 'left' | 'right' } | undefined
  >(undefined)

  useEffect(() => {
    try {
      window.localStorage.setItem(`columns.${tableKey}`, JSON.stringify(widths))
    } catch {
      // Kept for this session only, which is the honest degradation.
    }
  }, [tableKey, widths])

  useEffect(() => {
    if (!resizing) return undefined

    const move = (event: PointerEvent) => {
      const current = drag.current
      if (current === undefined) return
      // Measured from the column's own left edge, so the width is whatever the
      // pointer says rather than an accumulation of deltas that can drift.
      const width = Math.max(
        MIN_WIDTH,
        Math.round(
          current.edge === 'left' ? current.right - event.clientX : event.clientX - current.left,
        ),
      )
      setWidths((previous) => ({ ...previous, [current.key]: width }))
    }

    const release = () => {
      drag.current = undefined
      setResizing(false)
    }

    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', release)
    // A drag that ends outside the window, or is cancelled by the OS, must not
    // leave the table stuck following the pointer.
    document.addEventListener('pointercancel', release)
    return () => {
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', release)
      document.removeEventListener('pointercancel', release)
    }
  }, [resizing])

  const onGrab = useCallback(
    (key: string, event: React.PointerEvent<HTMLElement>, edge: 'left' | 'right' = 'right') => {
      // The handle sits inside a header. For a right-edge drag that header is
      // the column; for a left-edge drag the column is the next cell over --
      // the handle lives on the previous column, which has no width to change.
      const own = (event.currentTarget as HTMLElement).closest('th')
      const header = edge === 'left' ? own?.nextElementSibling : own
      if (header === null || header === undefined) return
      event.preventDefault()
      event.stopPropagation()
      const box = header.getBoundingClientRect()
      drag.current = { key, left: box.left, right: box.right, edge }
      setResizing(true)
    },
    [],
  )

  return {
    widthOf: (key) => widths[key],
    onGrab,
    resizing,
  }
}
